/* Package JSON Import will be here */
import React, { Fragment, useEffect, useCallback } from "react"
import { Button, Checkbox, Form, TextArea, Segment, Header } from "semantic-ui-react";
import SignatureCanvas from "react-signature-canvas"
/* Package JSON Import will be here */

/* Project Import will be here */
import { Constants } from "../../../config/appConstants";
import Loader from "../../sharedComponent/loader/loader";
import { AttestationModalUtils, formatAttestationDate } from "./AttestationUtils";
import { AttestationApprovedText } from "./RenderAttestation";
import ConfirmationPopup from "../TMVoucher/TMVoucherComponents/ConfirmationPopup";
import { Viewer, Worker } from "@react-pdf-viewer/core";
/* Project Import will be here */

const RenderSignature = React.memo(React.forwardRef((props, ref) => {
    return (
        <div style={{ cursor: props.attestationStatus ? "not-allowed" : "default" }}>
            <SignatureCanvas
                clearOnResize={false}
                ref={ref}
                onBegin={() => props.handleAttestationChange(true, Constants.DRAWING_SIGNATURE)}
                canvasProps={{
                    className: "sigCanvas shadow", style: {
                        pointerEvents: props.attestationStatus || props.isFromWeekTimeCard ? "none" : "auto",
                        opacity: props.attestationStatus ? 0.5 : 1,
                    },
                }}
            />
        </div>
    );
}));

const AttestationComponent = React.forwardRef((props, signatureRef) => {
    const { approvedAttestation, rejectionA, rejectionB, rejectionC, supervisorOverride, overrideReason, languageSelected } = props.attestation.attestationDetails;
    const { attestationStatus, otherReason, attachmentContent, stHours, otHours, dtHours } = props.attestation.attestationDetails;
    const { employeeName, startTime, endTime, lunchStartTime, lunchEndTime, createdDate, isSignatureExist } = props.attestation.attestationDetails
    const { popupRef } = props.attestation;
    const { yesText, noText, rejectionAText, rejectionBText, rejectionCText, isAttestationApproved, isAttestationRejected, isAttestationOverride } = AttestationModalUtils(approvedAttestation, supervisorOverride, languageSelected);
    const supervisorOverrideReasons = props.attestation.supervisorOverrideReasons;
    const isOtherSelected = overrideReason === Constants.OTHER;
    const disableDeleteBtn = !attestationStatus;
    const isFromWeekTimeCard = props?.attestation?.isFromWeekTimeCard ?? false;

    const attestationRejections = [
        { value: rejectionA, inputName: Constants.REJECTION_A, text: rejectionAText },
        { value: rejectionB, inputName: Constants.REJECTION_B, text: rejectionBText },
        { value: rejectionC, inputName: Constants.REJECTION_C, text: rejectionCText }
    ];

    const isSaveButtonDisable = props.attestation.isAttestationSaveBtnDisabled ||
        (!isAttestationOverride && !approvedAttestation) ||
        (isAttestationOverride && !overrideReason) ||
        (isAttestationOverride && isOtherSelected && !otherReason?.trim()) ||
        (!isSignatureExist);

    useEffect(() => {
        const signatureAttachment = `${Constants.PNG_BASE64}${attachmentContent}`;
        if (signatureRef.current) {
            signatureRef.current.fromDataURL(signatureAttachment);
        }
    }, [attachmentContent]);

    const handleAttestationChange = useCallback(props.handleAttestationChange, []);

    const renderClearButton = () => {
        return (
            <div className="text-right mb-2">
                <Button
                    style={{ fontSize: "12px" }}
                    onClick={() => props.clearSignature()}
                    className={`sigClearBtn ${isFromWeekTimeCard ? "pointerEvent-none" : ""}`}
                >
                    Clear
                </Button>
            </div>
        )
    }

    const renderAttestationHeader = () => (
        <Fragment>
            <div className="d-flex flex-column" style={{ gap: "1rem" }}>
                <div>{employeeName}</div>
                <div className="d-flex items-center" style={{ gap: "1.5rem" }}>
                    <span>{stHours} ST</span>
                    <span>{otHours} OT</span>
                    <span>{dtHours} DT</span>
                </div>
            </div>
            <div>{formatAttestationDate(createdDate)}</div>
        </Fragment>
    )

    const renderPunchesText = (text, value) => (
        <div className="text-center">
            <div>
                <span>{text}</span>
            </div>
            <div className="mt-1">
                <span>{value}</span>
            </div>
        </div>
    )

    const renderSupervisorAttestation = () => (
        <Fragment>
            <div className="d-flex justify-content items-center flex-column" style={{ marginBottom: "20px" }}>
                <p className="font-bold">Reason for override</p>
                <div className="w-50 attestDropdown">
                    <Form.Select className="hideDefaultText" style={{ width: "100%", border: "1px solid #aaa" }} options={supervisorOverrideReasons}
                        fluid
                        placeholder=" "
                        onChange={(_, { value }) => props.handleAttestationChange(value, Constants.OVERRIDE_REASON)}
                        value={overrideReason}
                        selectOnBlur={false}
                    />
                </div>
                {isOtherSelected &&
                    <div className="w-50 attestOtherInput">
                        <TextArea rows="2" className="textAreaInput maxHeightTextArea minHeightTextArea mt-2"
                            value={otherReason}
                            onChange={(_, data) => props.handleAttestationChange(data.value, Constants.ATTESTATION_OTHER_REASON)}
                            maxLength="50"
                        />
                    </div>
                }
            </div>
        </Fragment>
    )

    const renderEmployeeAttestation = () => (
        <Fragment>
            <div className="d-flex flex-row gap-2 items-center mb-10">
                <div className="w-70 attestDropdown">
                    {isFromWeekTimeCard
                        ?
                        <div style={{ border: "1px solid #aaa", padding: "0.5rem 1rem", borderRadius: "6px", fontWeight: "bold", maxWidth: "300px" }}>
                            Language Selected: {`${languageSelected}`}
                        </div>
                        :
                        <Form.Select style={{ border: "1px solid #aaa", fontWeight: "bold", maxWidth: "300px" }} options={props.attestation.attestationLanguages}
                            fluid
                            onChange={(_, { value }) => props.handleAttestationChange(value, Constants.LANGUAGE_SELECTED)}
                            value={languageSelected}
                            selectOnBlur={false}
                            text={`Language Selected: ${languageSelected}`}
                        />
                    }
                </div>
                <div className="w-15 d-flex justify-center font-bold">{yesText}</div>
                <div className="w-15 d-flex justify-content-start font-bold">{noText}</div>
            </div>

            <div className="d-flex flex-row gap-2 mb-2">
                <div className="w-70">{AttestationApprovedText(languageSelected)}</div>
                <div className="w-15 d-flex justify-center">
                    <Checkbox
                        className={`selectedTable ${isFromWeekTimeCard ? "pointerEvent-none" : ""}`}
                        checked={isAttestationApproved}
                        onChange={(_, data) => props.handleAttestationChange(data.checked, Constants.APPROVED_ATTESTATION, true)}
                    />
                </div>
                <div className="w-15 d-flex justify-content-start">
                    <Checkbox
                        className={`selectedTable ${isFromWeekTimeCard ? "pointerEvent-none" : ""}`}
                        checked={isAttestationRejected}
                        onChange={(e, data) => props.handleAttestationChange(data.checked, Constants.APPROVED_ATTESTATION)}
                    />
                </div>
            </div>
            {isAttestationRejected &&
                <div style={{ border: "1px solid black", padding: "0.5rem 0rem 0.5rem 0.5rem" }} className="mb-2">
                    {attestationRejections.map((rejection, i) => {
                        return <div className="d-flex flex-row gap-2 mb-10" key={`${approvedAttestation}${i}`}>
                            <div className="w-70">{rejection.text}</div>
                            <div className="w-15 d-flex justify-center">
                                <Checkbox
                                    className={`selectedTable ${isFromWeekTimeCard ? "pointerEvent-none" : ""}`}
                                    checked={rejection.value === Constants.ATTESTATION_YES}
                                    onChange={(e, data) => props.handleAttestationChange(data.checked, rejection.inputName, true)} />
                            </div>
                            <div className="w-15 d-flex justify-center"></div>
                        </div>
                    })}
                </div>
            }
        </Fragment>
    )

    const renderFooter = () => (
        <Fragment>
            {props.attestation.isFromWeekTimeCard ?
                <div className="d-flex align-items justify-center btnFooter" style={{ maxWidth: "750px" }}>
                    <Button style={{ fontSize: "12px", width: "100px" }} className="borderRadius" secondary size="small" onClick={() => props.goBack()}>Back</Button>
                </div>
                :
                <div className="d-flex align-items justify-content-between btnFooter" style={{ maxWidth: "750px" }}>
                    <Button style={{ fontSize: "12px", width: "100px" }} className="borderRadius" secondary size="small" onClick={() => props.openOrCloseAttestationBackModal(true)}>Back</Button>
                    <Button style={{ fontSize: "12px", width: "160px" }} className="borderRadius" disabled={disableDeleteBtn} secondary size="small" onClick={() => props.openOrCloseAttestationModals(Constants.ATTESTATION_OPEN_DELETE_MODAL)}>Delete Attestation</Button>
                    <Button style={{ fontSize: "12px", width: "100px" }} className="borderRadius" disabled={isSaveButtonDisable} secondary size="small" onClick={() => props.saveAttestation()}>Save</Button>
                </div>
            }
        </Fragment>
    )

    const attesationPdf = () => (
        <div style={{ width: "100%", height: "100%" }}>
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.2.228/build/pdf.worker.min.js">
                <Viewer
                    fileUrl={Constants.PDFDATA_BASE64 + props.attestation.attestationPDF}
                    defaultScale={1.2}
                />
            </Worker>
        </div>
    )

    return (
        <Segment.Group className="overflowScroll attestationSegment">
            <Segment className="stickyHeader zIndex-14">
                <Header as="h3" size="tiny" textAlign="center">{Constants.ATTESTATION_HEADER}</Header>
            </Segment>

            {props.attestation.isSubmittedAttestation
                ?
                <Fragment>
                    {attesationPdf()}
                </Fragment>
                :
                <div style={{ border: "2.2px solid #000", borderRadius: "0px", backgroundColor: "#fff", padding: "0 1.5rem 1.5rem 1.5rem", maxWidth: "750px", margin: "30px auto", marginBottom:"140px" }}>
                <Segment style={{ maxWidth: "750px", margin: "30px auto" }}>
                    <Fragment>
                        <Segment className="pb-3 color-black" style={{ backgroundColor: "#fff", padding: "0 1.5rem 1.5rem 1.5rem" }}>
                            {props.attestation.isShowAttestationLoader && <Loader />}
                            <div style={{ fontWeight: "bold" }} ref={popupRef} tabIndex={0}>
                                <div className="d-flex items-center justify-content-between" style={{ margin: "1rem 0" }}>
                                    {renderAttestationHeader()}
                                </div>
                                <div className="d-flex justify-content-between">
                                    {renderPunchesText(Constants.ATTESTATION_START_TIME_TEXT, startTime)}
                                    {renderPunchesText(Constants.ATTESTATION_LUNCH_START_TIME_TEXT, lunchStartTime)}
                                    {renderPunchesText(Constants.ATTESTATION_LUNCH_END_TIME_TEXT, lunchEndTime)}
                                    {renderPunchesText(Constants.ATTESTATION_END_TIME_TEXT, endTime)}
                                </div>
                            </div>

                            <div className="d-flex flex-row gap-2" style={{ margin: "20px 0" }}>
                                <div className="d-flex justify-center items-baseline w-full">
                                    <Checkbox
                                        className={`selectedTable ${isFromWeekTimeCard ? "pointerEvent-none" : ""}`}
                                        checked={supervisorOverride === Constants.ATTESTATION_YES}
                                        onChange={(e, data) => props.handleAttestationChange(data.checked, Constants.SUPERVISOR_OVERRIDE, true)}
                                    />
                                    <p className="w-100 ml-2">{Constants.SUPERVISOR_OVERRIDE_TEXT}</p>
                                </div>
                            </div>

                            <div style={{ border: "1px solid #eee", marginBottom: "20px" }}></div>

                            {isAttestationOverride ?
                                <Fragment>{renderSupervisorAttestation()}</Fragment>
                                :
                                <Fragment>{renderEmployeeAttestation()}</Fragment>
                            }

                            <div className="d-flex flexDirectionColumn align-items justify-content-center mt-1">
                                <div>
                                    {renderClearButton()}
                                    <RenderSignature
                                        ref={signatureRef}
                                        handleAttestationChange={handleAttestationChange}
                                        attestationStatus={attestationStatus}
                                        isFromWeekTimeCard={props.attestation.isFromWeekTimeCard}
                                    />
                                </div>
                            </div>
                        </Segment>
                    </Fragment>
                </Segment>
                </div>
            }

            <div className="d-flex justify-center">
                {renderFooter()}
            </div>

            <Fragment>
                {props.attestation.isShowDeleteAttestModal &&
                    <ConfirmationPopup
                        isShowConfirmation={props.attestation.isShowDeleteAttestModal}
                        confirmationHeader={Constants.WARNING_HEADER_MESSAGE}
                        confirmationMessage={Constants.CONFIRMATION_ATTESTATION_DELETED_MESSAGE}
                        noText={Constants.NO_TEXT}
                        yesText={Constants.YES_TEXT}
                        isShowYes={true}
                        onClickCloseBtn={() => props.openOrCloseAttestationModals(Constants.ATTESTATION_CLOSE_DELETE_MODAL)}
                        onClickYesBtn={() => props.onDeleteAttestation()}
                        isBtnDisabled={props.attestation.isAttestationDeleteBtnDisabled}
                    />
                }
                {props.attestation.isShowBackAttestationModal &&
                    <ConfirmationPopup
                        isShowConfirmation={props.attestation.isShowBackAttestationModal}
                        confirmationHeader={Constants.CONFIRMATION}
                        confirmationMessage={Constants.CONFIRMATION_POPUP_MESSAGE}
                        noText={Constants.NO_TEXT}
                        yesText={Constants.YES_TEXT}
                        isShowYes={true}
                        onClickCloseBtn={() => props.openOrCloseAttestationModals(Constants.ATTESTATION_CLOSE_BACK_MODAL)}
                        onClickYesBtn={() => props.saveAttestation()}
                        isBtnDisabled={props.attestation.isAttestationSaveBtnDisabled}
                    />
                }
                {props.attestation.isShowSwitchingAttestModal &&
                    <ConfirmationPopup
                        isShowConfirmation={props.attestation.isShowSwitchingAttestModal}
                        confirmationHeader={Constants.WARNING_HEADER_MESSAGE}
                        confirmationMessage={props.attestation.attestationSwitchingMessage}
                        noText={Constants.NO_TEXT}
                        yesText={Constants.YES_TEXT}
                        isShowYes={true}
                        onClickCloseBtn={() => props.openOrCloseAttestationModals(Constants.ATTESTATION_SWITCHING_MODAL)}
                        onClickYesBtn={() => props.handleSuperVisorChange()}
                        isBtnDisabled={props.attestation.isAttestationDeleteBtnDisabled}
                    />
                }
                {
                    props.attestation.isShowAttestationErrorModal &&
                    <ConfirmationPopup
                        isShowConfirmation={props.attestation.isShowAttestationErrorModal}
                        confirmationMessage={props.attestation.attestErrorValidationMessage}
                        confirmationHeader={Constants.WARNING_HEADER_MESSAGE}
                        noText={Constants.BUTTON_BACK}
                        isShowYes={false}
                        onClickCloseBtn={() => props.openOrCloseAttestationModals(Constants.ATTESTATION_ERROR_MODAL)}
                    />
                }
                {props.attestation.isShowEditAttestationConfirmationModal &&
                    <ConfirmationPopup
                        isShowConfirmation={props.attestation.isShowEditAttestationConfirmationModal}
                        confirmationHeader={Constants.WARNING_HEADER_MESSAGE}
                        confirmationMessage={props.attestation.attestationEditConfirmationMessage}
                        noText={Constants.NO_TEXT}
                        yesText={Constants.YES_TEXT}
                        isShowYes={true}
                        onClickCloseBtn={() => props.openOrCloseAttestationModals(Constants.ATTESTATION_EDIT_CONFIRMATION_MODAL)}
                        onClickYesBtn={() => props.handleAttestationEditChange()}
                        isBtnDisabled={props.attestation.isAttestationDeleteBtnDisabled}
                    />
                }
            </Fragment>
        </Segment.Group>
    )
})

export default AttestationComponent;